import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { services } from "../data/services";
import { Reveal, MaskLines } from "./ui/Primitives";

const Services: React.FC = () => {
  const [open, setOpen] = useState<string | null>(services[0].slug);

  return (
    <>
      <section className="bg-ink pb-14 pt-28 lg:pb-20 lg:pt-36">
        <div className="shell">
          <MaskLines
            as="h1"
            className="t-display max-w-[12ch] text-fg"
            lines={["מה אנחנו", "יודעים לעשות"]}
          />
          <Reveal delay={140}>
            <p className="t-lead mt-8 max-w-[52ch]">
              מפיתוח אתרים ואפליקציות מובייל, דרך דשבורדים וסוכנים חכמים, ועד
              קמפיינים ממומנים ואסטרטגיה דיגיטלית מלאה. הכל תחת קורת גג אחת, בלי
              קבלני משנה.
            </p>
          </Reveal>
        </div>
      </section>

      {/* An index you can open, not eleven identical cards. */}
      <section className="bg-ink pb-24 lg:pb-32">
        <div className="shell">
          <ul className="border-t border-[var(--line)]">
            {services.map((service, i) => {
              const isOpen = open === service.slug;
              const panelId = `svc-panel-${service.slug}`;

              return (
                <Reveal as="li" key={service.slug} delay={Math.min(i, 6) * 45}>
                  <div className="border-b border-[var(--line)]">
                    <h2>
                      <button
                        type="button"
                        onClick={() => setOpen(isOpen ? null : service.slug)}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        className="group grid w-full grid-cols-[2.5rem_minmax(0,1fr)_1rem] items-start gap-4 py-6 text-right sm:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.2fr)_1rem] sm:items-baseline sm:gap-6"
                      >
                        <span
                          className={`t-data mt-1.5 text-xs transition-colors duration-300 sm:mt-0 ${
                            isOpen ? "text-signal" : "text-fg-3"
                          }`}
                          dir="ltr"
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>

                        <span className="min-w-0">
                          <span
                            className={`t-h3 block transition-colors duration-300 ${
                              isOpen ? "text-signal" : "text-fg group-hover:text-signal"
                            }`}
                          >
                            {service.title}
                          </span>
                          <span className="mt-1.5 block text-sm text-fg-2 sm:hidden">
                            {service.short}
                          </span>
                        </span>

                        <span className="hidden min-w-0 text-sm text-fg-2 sm:block">
                          {service.short}
                        </span>

                        <Plus
                          aria-hidden="true"
                          className={`mt-1.5 h-4 w-4 transition-transform duration-500 ease-out-expo sm:mt-0 ${
                            isOpen ? "rotate-45 text-signal" : "text-fg-3"
                          }`}
                        />
                      </button>
                    </h2>

                    <div
                      id={panelId}
                      role="region"
                      aria-label={service.title}
                      hidden={!isOpen}
                      className="pb-10 ps-0 sm:ps-[3.5rem]"
                    >
                      <p className="t-body mb-6">{service.description}</p>

                      <ul className="grid gap-x-10 gap-y-2.5 md:grid-cols-2">
                        {service.details.map((detail) => (
                          <li
                            key={detail}
                            className="flex items-start gap-3 text-sm text-fg-2"
                          >
                            <span
                              aria-hidden="true"
                              className="mt-[0.6em] h-px w-3 shrink-0 bg-signal"
                            />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1.5">
                        {service.tags.map((tag) => (
                          <span
                            key={tag}
                            className="t-data text-[0.65rem] tracking-widest text-fg-3"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </ul>

          <Reveal delay={120}>
            <div className="mt-14 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact" className="btn btn-primary">
                <span>בואו נדבר על הפרויקט שלכם</span>
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <Link to="/portfolio" className="btn btn-ghost">
                צפו בעבודות שלנו
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Services;
